import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Loader2, Plus, Tag, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import { useToast } from '@/stores/toast'
import type { PartnerGroup, PartnerPrice } from '@/types/models'
import { Modal } from '@/components/Modal'
import { ItemPicker } from '@/components/ItemPicker'
import { SearchBox } from '@/components/SearchBox'
import { cn, formatNum, todayISO } from '@/lib/utils'
import { useCrudMutations } from '@/lib/useCrudMutations'
import { useFormFieldNavigation } from '@/lib/useFormFieldNavigation'

const PRICE_FORM_FIELDS = ['item', 'sale_price', 'effective_from', 'effective_to', 'memo'] as const

interface PriceForm {
  id?: number
  group: number | null
  item: number | null
  // number 로 두면 초기값 0 이 필드에 "0" 으로 남아 뒤에 입력 시 "0500" 이 됨.
  // 문자열로 두고 빈 칸을 허용, 저장 시점에 정규화한다.
  sale_price: string
  effective_from: string
  effective_to: string | null   // 비우면(무기한) null
  memo: string
  is_active: boolean
}

export function PricesTab() {

  const [groupSearch, setGroupSearch] = useState('')
  const [itemSearch, setItemSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')   // 기간 필터 시작
  const [dateTo, setDateTo] = useState('')       // 기간 필터 끝
  const [groupId, setGroupId] = useState<number | null>(null)
  const [edit, setEdit] = useState<PriceForm | null>(null)
  // 체크한 단가 id — 선택 삭제용. 화면에 안 보이는(필터로 빠진) id 는 삭제 대상에서 제외한다.
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [lastIdx, setLastIdx] = useState<number | null>(null)   // Shift+클릭 구간 선택 기준 행

  const { data: groups = [] } = useQuery({
    queryKey: ['partner-groups-list', groupSearch],
    queryFn: () => {
      const params = new URLSearchParams()
      if (groupSearch) params.set('q', groupSearch)
      return api<PartnerGroup[]>(`/api/partner-groups?${params}`)
    },
  })

  // 검색 결과 바뀌면 첫 그룹 자동 선택
  useEffect(() => {
    if (!groups.length) return
    if (!groupId || !groups.some((g) => g.id === groupId)) {
      setGroupId(groups[0].id)
    }
  }, [groups, groupId])

  const { data: prices = [] } = useQuery({
    queryKey: ['prices', groupId, itemSearch, dateFrom, dateTo],
    queryFn: () => {
      if (!groupId) return Promise.resolve([] as PartnerPrice[])
      const params = new URLSearchParams({ group: String(groupId) })
      if (itemSearch) params.set('q', itemSearch)
      if (dateFrom) params.set('date_from', dateFrom)
      if (dateTo) params.set('date_to', dateTo)
      return api<PartnerPrice[]>(`/api/prices?${params}`)
    },
    enabled: !!groupId,
  })

  const { save, remove } = useCrudMutations<PriceForm>({
    apiBase: '/api/prices',
    queryKey: 'prices',
    onSaved: () => setEdit(null),
  })

  const { formRef, onKeyDown: onFormKeyDown } = useFormFieldNavigation(PRICE_FORM_FIELDS)

  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const currentGroup = groups.find((g) => g.id === groupId)

  // 상위 거래처를 바꾸면 선택 초기화.
  useEffect(() => { setSelected(new Set()); setLastIdx(null) }, [groupId])
  // 목록이 바뀌면(필터 변경) 구간 선택 기준 행을 버린다 — 행 번호가 달라지므로.
  useEffect(() => { setLastIdx(null) }, [itemSearch, dateFrom, dateTo])
  const selectedIds = prices.filter((p) => selected.has(p.id)).map((p) => p.id)
  const allChecked = prices.length > 0 && selectedIds.length === prices.length
  function toggle(id: number, idx: number, shift: boolean) {
    // Shift+클릭: 직전 클릭한 행 ~ 현재 행 범위를 (클릭한 행의 새 상태에 맞춰) 한 번에 — 출고처리와 동일.
    if (shift && lastIdx !== null && lastIdx !== idx) {
      const [a, b] = idx < lastIdx ? [idx, lastIdx] : [lastIdx, idx]
      const rangeIds = prices.slice(a, b + 1).map((p) => p.id)
      const check = !selected.has(id)
      setSelected((cur) => {
        const next = new Set(cur)
        for (const rid of rangeIds) { if (check) next.add(rid); else next.delete(rid) }
        return next
      })
      setLastIdx(idx)
      return
    }
    setSelected((cur) => {
      const next = new Set(cur)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
    setLastIdx(idx)
  }

  // 단가 일괄 삭제 — 견적 잘못 등록 시 되돌리기용.
  // ids 를 주면 체크한 것만(선택 삭제), 없으면 지금 화면에 보이는(필터된) 단가 전부(전체 삭제).
  async function bulkDelete(ids?: number[]) {
    const target = ids ?? prices.map((p) => p.id)
    if (!groupId || !target.length) return
    const period = dateFrom || dateTo ? ` · 적용기간 ${dateFrom || '~'}~${dateTo || '무기한'}` : ''
    const what = ids ? `선택한 단가 ${target.length}건을` : `지금 보이는 단가 ${target.length}건을 전체`
    const ok = await confirm(
      `"${currentGroup?.name}"${period}\n${what} 삭제할까요? 되돌릴 수 없습니다.`,
      { danger: true },
    )
    if (!ok) return
    setBulkDeleting(true)
    try {
      const res = await api<{ deleted: number }>('/api/prices/bulk-delete', {
        method: 'POST', body: { ids: target },
      })
      push(`단가 ${res.deleted}건 삭제 완료`, 'success')
      setSelected(new Set()); setLastIdx(null)
      qc.invalidateQueries({ queryKey: ['prices'] })
    } catch (e: any) {
      push(e?.message || '삭제 실패', 'error')
    } finally {
      setBulkDeleting(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <header className="mb-5 flex items-start gap-3">
        <Tag className="w-6 h-6 text-emerald-600 mt-0.5"/>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">상위 거래처별 단가등록</h2>
          <p className="text-sm text-slate-500 mt-1">
            상위 거래처를 좌측에서 선택하고 우측에서 품목별 매출/매입 단가를 등록합니다.
            발주서 처리 시 상위 거래처의 단가가 소속 자식 매출처 전부에 자동 적용됩니다.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-12 gap-4">
        {/* LEFT — 상위 거래처 리스트 */}
        <div className="col-span-4 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-3 border-b border-slate-100 bg-slate-50">
            <SearchBox value={groupSearch} onChange={setGroupSearch} placeholder="상위 거래처 검색" />
          </div>
          <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
            {groups.map((g) => (
              <button
                key={g.id}
                onClick={() => setGroupId(g.id)}
                className={cn(
                  'w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-blue-50',
                  groupId === g.id && 'bg-blue-100 border-l-4 border-l-blue-500'
                )}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-3.5 h-3.5 text-blue-500 shrink-0"/>
                  <span className="text-sm font-medium text-slate-800 truncate flex-1">{g.name}</span>
                  <span className="text-[10px] text-slate-400 tabular-nums shrink-0">
                    {g.member_count}개
                  </span>
                </div>
              </button>
            ))}
            {!groups.length && (
              <div className="text-center text-slate-400 py-8 text-sm">상위 거래처가 없습니다</div>
            )}
          </div>
        </div>

        {/* RIGHT — 단가 리스트 */}
        <div className="col-span-8 bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-3 border-b border-slate-100 bg-slate-50 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[8rem]">
              <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold">선택된 상위 거래처</div>
              <div className="text-sm font-bold text-slate-900 mt-0.5">
                {currentGroup
                  ? `${currentGroup.name} · ${currentGroup.member_count}개 자식`
                  : '상위 거래처를 선택하세요'}
              </div>
            </div>
            {/* 적용기간(범위) 필터 — 이 기간과 겹치는 단가만 표시 */}
            <div className="flex items-center gap-1">
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                     title="적용기간 시작"
                     className="px-2 py-1 border border-slate-300 rounded text-xs tabular-nums bg-white"/>
              <span className="text-slate-400 text-xs">~</span>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                     title="적용기간 끝"
                     className="px-2 py-1 border border-slate-300 rounded text-xs tabular-nums bg-white"/>
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(''); setDateTo('') }}
                        title="기간 필터 해제"
                        className="text-slate-400 hover:text-rose-500 px-1 text-sm leading-none">✕</button>
              )}
            </div>
            <SearchBox value={itemSearch} onChange={setItemSearch} placeholder="품목 검색" className="w-40" />
            {selectedIds.length > 0 && (
              <button
                onClick={() => bulkDelete(selectedIds)}
                disabled={bulkDeleting}
                title="체크한 단가만 삭제"
                className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1"
              >
                {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                선택 삭제 ({selectedIds.length})
              </button>
            )}
            {prices.length > 0 && (
              <button
                onClick={() => bulkDelete()}
                disabled={bulkDeleting}
                title="지금 보이는 단가를 한 번에 삭제 (견적 잘못 등록 시 되돌리기)"
                className="bg-white hover:bg-rose-50 disabled:opacity-50 border border-rose-300 text-rose-600 text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1"
              >
                {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                전체 삭제 ({prices.length})
              </button>
            )}
            <button
              onClick={() => setEdit({
                group: groupId, item: null,
                sale_price: '',
                effective_from: todayISO(), effective_to: null, memo: '', is_active: true,
              })}
              disabled={!groupId}
              data-role="add-btn"
              title="Alt+N"
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-blue-300 text-white text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> 단가 추가
            </button>
          </div>

          <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide sticky top-0">
                <tr>
                  <th className="pl-3 py-2 w-8">
                    <input type="checkbox" checked={allChecked} disabled={!prices.length}
                           title="보이는 단가 전체 선택"
                           onChange={(e) => setSelected(e.target.checked ? new Set(prices.map((p) => p.id)) : new Set())}/>
                  </th>
                  <th className="px-3 py-2 text-left font-medium w-32">품목코드</th>
                  <th className="px-3 py-2 text-left font-medium">품명</th>
                  <th className="px-3 py-2 text-left font-medium w-20">규격</th>
                  <th className="px-3 py-2 text-left font-medium w-12">단위</th>
                  <th className="px-3 py-2 text-right font-medium w-24">매출단가</th>
                  <th className="px-3 py-2 text-left font-medium w-28">적용시작일</th>
                  <th className="px-3 py-2 text-left font-medium w-28">적용종료일</th>
                  <th className="px-3 py-2 text-right font-medium w-20"></th>
                </tr>
              </thead>
              <tbody>
                {prices.map((r, idx) => (
                  <tr key={r.id} className={cn('border-t border-slate-100 hover:bg-slate-50', selected.has(r.id) && 'bg-rose-50/60')}>
                    <td className="pl-3 py-1.5">
                      <input type="checkbox" checked={selected.has(r.id)} readOnly
                             onClick={(e) => toggle(r.id, idx, e.shiftKey)}
                             title="Shift+클릭: 이전 선택부터 여기까지 한 번에"
                             className="cursor-pointer"/>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs text-slate-600 tabular-nums">{r.item_code}</td>
                    <td className="px-3 py-1.5 text-slate-900">{r.item_name}</td>
                    <td className="px-3 py-1.5 text-slate-500 text-xs">{r.item_spec}</td>
                    <td className="px-3 py-1.5 text-slate-500 text-xs">{r.item_unit}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{formatNum(r.sale_price)}</td>
                    <td className="px-3 py-1.5 text-slate-500 text-xs tabular-nums">{r.effective_from}</td>
                    <td className="px-3 py-1.5 text-slate-500 text-xs tabular-nums">{r.effective_to || '무기한'}</td>
                    <td className="px-3 py-1.5 text-right">
                      <button onClick={() => setEdit({
                        id: r.id, group: r.group, item: r.item,
                        sale_price: r.sale_price,
                        effective_from: r.effective_from, effective_to: r.effective_to,
                        memo: r.memo, is_active: r.is_active,
                      })} className="text-blue-600 hover:text-blue-800 text-xs mr-2">수정</button>
                      <button onClick={() => { void confirm('이 단가를 삭제할까요?', { danger: true }).then((ok) => ok && remove.mutate(r.id)) }}
                              className="text-rose-500 hover:text-rose-700 text-xs">삭제</button>
                    </td>
                  </tr>
                ))}
                {!prices.length && groupId && (
                  <tr><td colSpan={9} className="text-center text-slate-400 py-10 text-sm">등록된 단가가 없습니다.</td></tr>
                )}
                {!groupId && (
                  <tr><td colSpan={9} className="text-center text-slate-400 py-10 text-sm">좌측에서 상위 거래처를 선택하세요.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? '단가 수정' : '단가 추가'}>
        {edit && (
          <form ref={formRef} onKeyDown={onFormKeyDown}
                onSubmit={(e) => {
                  e.preventDefault()
                  save.mutate({
                    ...edit,
                    // 빈 칸은 0 으로, 그 외엔 입력 문자열 그대로 전송(DRF DecimalField가 파싱)
                    sale_price: edit.sale_price.trim() || '0',
                    // 종료일 비우면 무기한 → null 전송
                    effective_to: edit.effective_to?.trim() ? edit.effective_to : null,
                  })
                }}
                className="space-y-3">
            <div data-form-field="item">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">품목 *</label>
              <div className="border border-slate-300 rounded-lg">
                <ItemPicker
                  value={edit.item}
                  onChange={(it) => setEdit({ ...edit, item: it.id })}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">매출단가 *</label>
              <input data-form-field="sale_price" type="number" step="0.01" value={edit.sale_price}
                     onChange={(e) => setEdit({ ...edit, sale_price: e.target.value })} required
                     className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-right tabular-nums"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">적용 시작일 *</label>
                <input data-form-field="effective_from" type="date" value={edit.effective_from}
                       onChange={(e) => setEdit({ ...edit, effective_from: e.target.value })} required
                       className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm tabular-nums"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1 block">적용 종료일 <span className="text-slate-400 font-normal">(비우면 무기한)</span></label>
                <input data-form-field="effective_to" type="date" value={edit.effective_to ?? ''}
                       onChange={(e) => setEdit({ ...edit, effective_to: e.target.value || null })}
                       className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm tabular-nums"/>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">비고</label>
              <input data-form-field="memo" value={edit.memo} onChange={(e) => setEdit({ ...edit, memo: e.target.value })}
                     className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit" disabled={!edit.item}
                      className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-blue-300 text-white font-semibold rounded-lg">저장</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
